import { useCallback, useEffect, useRef, useState } from 'react'
import * as z from 'zod'

import { MentionItemSchema } from '@/components/stella/schema'
import type { MentionItem } from '@/components/stella/types'

export const DEFAULT_RAIL_WIDTH = 360

const STORAGE_KEY_OPEN = 'stella:isOpen'
const STORAGE_KEY_CONVERSATION = 'stella:currentConversation'
const STORAGE_KEY_RAIL_WIDTH = 'stella:railWidth'
const STORAGE_KEY_DRAFT = 'stella:draft'
const STORAGE_KEY_MENTIONS = 'stella:mentions'
const STORAGE_KEY_HIDDEN_MENTIONS = 'stella:hiddenMentions'
const STORAGE_KEY_HIDDEN_MENTION_ANCHOR_IDS = 'stella:hiddenMentionAnchorIds'
const DRAFT_DEBOUNCE_MS = 300

const STORAGE_KEYS = [
  STORAGE_KEY_OPEN,
  STORAGE_KEY_CONVERSATION,
  STORAGE_KEY_RAIL_WIDTH,
  STORAGE_KEY_DRAFT,
  STORAGE_KEY_MENTIONS,
  STORAGE_KEY_HIDDEN_MENTIONS,
  STORAGE_KEY_HIDDEN_MENTION_ANCHOR_IDS,
]

const readStorage = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStorage = (key: string, value: string | null) => {
  try {
    if (value === null) {
      sessionStorage.removeItem(key)
    } else {
      sessionStorage.setItem(key, value)
    }
  } catch (error) {
    console.warn('[useStellaSession] sessionStorage write failed:', error)
  }
}

export const clearStellaSessionStorage = () => {
  for (const key of STORAGE_KEYS) {
    writeStorage(key, null)
  }
}

const readStoredList = <Item>(key: string, schema: z.ZodType<Item>): Item[] => {
  const stored = readStorage(key)
  if (!stored) {
    return []
  }

  try {
    const parsed = z.array(schema).safeParse(JSON.parse(stored))
    return parsed.success ? parsed.data : []
  } catch {
    return []
  }
}

const isMissingAnAnchor = (mentions: MentionItem[], anchorIds: string[]) =>
  anchorIds.some(
    (anchorId) => !mentions.some((mention) => mention.id === anchorId)
  )

const cancelPendingDraftWrite = (draftTimerRef: {
  current: ReturnType<typeof setTimeout> | null
}) => {
  if (draftTimerRef.current) {
    clearTimeout(draftTimerRef.current)
  }
}

export interface StellaSession {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  railWidth: number
  setRailWidth: (width: number | ((previous: number) => number)) => void
  currentConversationId: string | null
  setCurrentConversationId: (id: string | null) => void
  initialDraft: string
  setInitialDraft: (value: string) => void
  setDraft: (value: string) => void
  setInitialMentions: (
    value: MentionItem[],
    hiddenMentions?: MentionItem[]
  ) => void
  takeHiddenMentions: () => MentionItem[]
  initialMentions: MentionItem[]
  initialValueVersion?: number
  setMentions: (mentions: MentionItem[]) => void
  initialDraftVersion: number
}

interface UseStellaSessionProps {
  defaultRailWidth?: number
}

export const useStellaSession = ({
  defaultRailWidth = DEFAULT_RAIL_WIDTH,
}: UseStellaSessionProps): StellaSession => {
  const [isOpen, setIsOpenState] = useState<boolean>(
    () => readStorage(STORAGE_KEY_OPEN) === 'true'
  )

  const [currentConversationId, setCurrentConversationIdState] = useState<
    string | null
  >(() => readStorage(STORAGE_KEY_CONVERSATION))

  const [railWidth, setRailWidthState] = useState<number>(() => {
    const stored = readStorage(STORAGE_KEY_RAIL_WIDTH)
    const parsed = stored !== null ? Number(stored) : NaN
    return Number.isFinite(parsed) ? parsed : defaultRailWidth
  })

  const initialDraftRef = useRef<string>(readStorage(STORAGE_KEY_DRAFT) ?? '')
  const initialMentionsRef = useRef<MentionItem[]>(
    readStoredList(STORAGE_KEY_MENTIONS, MentionItemSchema)
  )
  const hiddenMentionsRef = useRef<MentionItem[]>(
    readStoredList(STORAGE_KEY_HIDDEN_MENTIONS, MentionItemSchema)
  )
  const hiddenMentionAnchorIdsRef = useRef<string[]>(
    readStoredList(STORAGE_KEY_HIDDEN_MENTION_ANCHOR_IDS, z.string())
  )
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [initialDraftVersion, setInitialDraftVersion] = useState(0)

  const setIsOpen = useCallback((open: boolean) => {
    setIsOpenState(open)
    writeStorage(STORAGE_KEY_OPEN, open ? 'true' : null)
  }, [])

  const setCurrentConversationId = useCallback((id: string | null) => {
    setCurrentConversationIdState(id)
    writeStorage(STORAGE_KEY_CONVERSATION, id)
  }, [])

  const setRailWidth = useCallback(
    (width: number | ((previous: number) => number)) => {
      setRailWidthState((previous) => {
        const next = typeof width === 'function' ? width(previous) : width
        writeStorage(STORAGE_KEY_RAIL_WIDTH, String(next))
        return next
      })
    },
    []
  )

  const takeHiddenMentions = useCallback(() => {
    const hiddenMentions = hiddenMentionsRef.current
    hiddenMentionsRef.current = []
    hiddenMentionAnchorIdsRef.current = []
    writeStorage(STORAGE_KEY_HIDDEN_MENTIONS, null)
    writeStorage(STORAGE_KEY_HIDDEN_MENTION_ANCHOR_IDS, null)
    return hiddenMentions
  }, [])

  const setInitialDraft = useCallback((value: string) => {
    cancelPendingDraftWrite(draftTimerRef)
    writeStorage(STORAGE_KEY_DRAFT, value || null)
    initialDraftRef.current = value
    setInitialDraftVersion((previous) => previous + 1)
  }, [])

  const setDraft = useCallback(
    (value: string) => {
      initialDraftRef.current = value
      cancelPendingDraftWrite(draftTimerRef)

      if (!value.trim()) {
        takeHiddenMentions()
      }

      draftTimerRef.current = setTimeout(() => {
        writeStorage(STORAGE_KEY_DRAFT, value || null)
      }, DRAFT_DEBOUNCE_MS)
    },
    [takeHiddenMentions]
  )

  const setInitialMentions = useCallback(
    (value: MentionItem[], hiddenMentions: MentionItem[] = []) => {
      const hasHiddenMentions = hiddenMentions.length > 0
      const anchorIds = hasHiddenMentions
        ? value.map((mention) => mention.id)
        : []
      writeStorage(STORAGE_KEY_MENTIONS, JSON.stringify(value))
      writeStorage(
        STORAGE_KEY_HIDDEN_MENTIONS,
        hasHiddenMentions ? JSON.stringify(hiddenMentions) : null
      )
      writeStorage(
        STORAGE_KEY_HIDDEN_MENTION_ANCHOR_IDS,
        hasHiddenMentions ? JSON.stringify(anchorIds) : null
      )
      initialMentionsRef.current = value
      hiddenMentionsRef.current = hiddenMentions
      hiddenMentionAnchorIdsRef.current = anchorIds
      setInitialDraftVersion((previous) => previous + 1)
    },
    []
  )

  const setMentions = useCallback(
    (next: MentionItem[]) => {
      initialMentionsRef.current = next

      if (
        next.length === 0 ||
        isMissingAnAnchor(next, hiddenMentionAnchorIdsRef.current)
      ) {
        takeHiddenMentions()
      }

      writeStorage(
        STORAGE_KEY_MENTIONS,
        next.length > 0 ? JSON.stringify(next) : null
      )
    },
    [takeHiddenMentions]
  )

  useEffect(() => () => cancelPendingDraftWrite(draftTimerRef), [])

  return {
    isOpen,
    setIsOpen,
    railWidth,
    setRailWidth,
    currentConversationId,
    setCurrentConversationId,
    initialDraft: initialDraftRef.current,
    setInitialDraft,
    setDraft,
    setInitialMentions,
    takeHiddenMentions,
    initialMentions: initialMentionsRef.current,
    setMentions,
    initialDraftVersion,
  }
}
